import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState, memo, type FC } from "react";
import { getPhotos } from "../lib/api";
import type { Photo } from "../components/gallery/types";

const SERVER_URL = import.meta.env.VITE_API_URL;

interface PresentationProps {
    photos: Photo[];
    interval: number;
}

const PresentationSlideshow: FC<PresentationProps> = memo(({ photos, interval }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);

    // Automatické přepínání fotek
    useEffect(() => {
        if (photos.length === 0) return;

        const intervalId = setInterval(() => {
            setIsTransitioning(true);
            setTimeout(() => {
                setCurrentIndex((prevIndex) => {
                    // Náhodný výběr další fotky
                    const availableIndices = Array.from({ length: photos.length }, (_, i) => i);
                    availableIndices.splice(prevIndex, 1); // Odebereme aktuální index
                    const randomIndex = availableIndices[Math.floor(Math.random() * availableIndices.length)];
                    return randomIndex || 0;
                });
                setIsTransitioning(false);
            }, 1000);
        }, interval * 1000);

        return () => clearInterval(intervalId);
    }, [photos.length, interval]);

    // Obsluha klávesových zkratek
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") {
                setCurrentIndex((prev) => prev > 0 ? prev - 1 : photos.length - 1);
            } else if (e.key === "ArrowRight") {
                setCurrentIndex((prev) => prev < photos.length - 1 ? prev + 1 : 0);
            } else if (e.key === "Escape") {
                window.close();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [photos.length]);

    if (photos.length === 0) {
        return (
            <div className="w-full h-full flex items-center justify-center bg-black text-white">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent mb-4" />
                    <p className="text-xl">Načítám fotky...</p>
                </div>
            </div>
        );
    }

    const currentPhoto = photos[currentIndex];

    return (
        <div className="w-full h-full relative bg-black overflow-hidden">
            {/* Aktuální fotka */}
            <div
                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${isTransitioning ? "opacity-0" : "opacity-100"
                    }`}
            >
                <img
                    src={currentPhoto.url}
                    alt={`Svatební fotka od ${currentPhoto.uploadedBy}`}
                    className="w-full h-full object-contain"
                />
            </div>

            {/* Jméno uploaderá dole */}
            <div className="absolute bottom-4 left-4 text-white">
                <p className="text-sm opacity-80 bg-black/50 px-2 py-1 rounded">
                    {currentPhoto.uploadedBy}
                </p>
            </div>

            {/* Indikátor pozice */}
            <div className="absolute top-8 right-8 text-white text-lg opacity-75">
                {currentIndex + 1} / {photos.length}
            </div>

            {/* Návod pro ovládání */}
            <div className="absolute top-8 left-8 text-white text-sm opacity-50">
                <p>← → Navigace | ESC Zavřít</p>
            </div>
        </div>
    );
});

const RouteComponent: FC = memo(() => {
    const [photos, setPhotos] = useState<Photo[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [slideInterval, setSlideInterval] = useState(8); // sekund

    const loadPhotos = useCallback(async () => {
        try {
            const result = await getPhotos();
            const photosWithFullUrl = Array.isArray(result)
                ? result.map((photo: Photo) => ({
                    ...photo,
                    url: photo.url.startsWith("http")
                        ? photo.url
                        : `${SERVER_URL.endsWith("/") ? SERVER_URL : `${SERVER_URL}/`}${photo.url}`,
                    thumbnail: photo.thumbnail.startsWith("http")
                        ? photo.thumbnail
                        : `${SERVER_URL.endsWith("/") ? SERVER_URL : `${SERVER_URL}/`}${photo.thumbnail}`,
                }))
                : [];

            // Zamícháme fotky náhodně
            const shuffledPhotos = [...photosWithFullUrl].sort(() => 0.5 - Math.random());
            setPhotos(shuffledPhotos);
        } catch (error) {
            console.error("Chyba při načítání fotek:", error);
            setError(
                error instanceof Error ? error.message : "Nepodařilo se načíst fotky",
            );
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Načtení fotek při prvním načtení
    useEffect(() => {
        loadPhotos();
    }, [loadPhotos]);

    // Automatické obnovování seznamu fotek každou minutu
    useEffect(() => {
        const updateInterval = setInterval(() => {
            loadPhotos();
        }, 60000); // 60 sekund

        return () => clearInterval(updateInterval);
    }, [loadPhotos]);

    // Full screen mode
    useEffect(() => {
        const enterFullscreen = async () => {
            try {
                if (document.documentElement.requestFullscreen) {
                    await document.documentElement.requestFullscreen();
                }
            } catch (error) {
                console.log("Fullscreen not supported or failed:", error);
            }
        };

        enterFullscreen();

        // Cleanup při opuštění stránky
        return () => {
            if (document.fullscreenElement) {
                document.exitFullscreen().catch(console.error);
            }
        };
    }, []);

    if (isLoading) {
        return (
            <div className="w-screen h-screen flex items-center justify-center bg-black text-white">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent mb-4" />
                    <p className="text-xl">Načítám prezentaci...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-screen h-screen flex items-center justify-center bg-black text-white">
                <div className="text-center">
                    <p className="text-xl text-red-400 mb-4">Chyba při načítání fotek</p>
                    <p className="text-lg">{error}</p>
                    <button
                        onClick={loadPhotos}
                        className="mt-4 px-6 py-2 bg-rose-600 text-white rounded hover:bg-rose-700 transition-colors"
                    >
                        Zkusit znovu
                    </button>
                </div>
            </div>
        );
    }

    if (photos.length === 0) {
        return (
            <div className="w-screen h-screen flex items-center justify-center bg-black text-white">
                <div className="text-center">
                    <p className="text-xl mb-4">Zatím nejsou nahrané žádné fotky</p>
                    <p className="text-lg opacity-75">Prezentace se spustí, jakmile někdo nahraje fotky</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-screen h-screen">
            {/* Ovládací panel (skrytý, zobrazí se při hover) */}
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 opacity-0 hover:opacity-100 transition-opacity duration-300">
                <div className="bg-black/50 text-white px-4 py-2 rounded-lg flex items-center gap-4">
                    <label className="text-sm">
                        Interval:
                        <select
                            value={slideInterval}
                            onChange={(e) => setSlideInterval(Number(e.target.value))}
                            className="ml-2 px-2 py-1 bg-black/50 text-white rounded"
                        >
                            <option value={3}>3s</option>
                            <option value={5}>5s</option>
                            <option value={8}>8s</option>
                            <option value={10}>10s</option>
                            <option value={15}>15s</option>
                        </select>
                    </label>
                    <button
                        onClick={loadPhotos}
                        className="px-3 py-1 bg-rose-600 text-white rounded hover:bg-rose-700 transition-colors text-sm"
                    >
                        Obnovit
                    </button>
                </div>
            </div>

            <PresentationSlideshow photos={photos} interval={slideInterval} />
        </div>
    );
});

export const Route = createFileRoute("/presentation")({
    component: RouteComponent,
}); 